import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { ShieldCheck } from 'lucide-react';

import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { DashLogo } from '@/design-system/dash-logo';
import { toast } from '@/design-system/toaster';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axios';
import { storage, getUserData } from '@/utils/storage';
import { APP_CONFIG, getAllowedDomainLabel } from '@/config/app';

const debugAuth = () => {
  const token = storage.getItem('token');
  const userData = getUserData();
  console.log('token:', token ? token.substring(0, 20) + '...' : 'None');
  console.log('profile:', userData);
};

if (typeof window !== 'undefined') {
  (window as any).debugAuth = debugAuth;
}

const Index: React.FC = () => {
  const { user, token, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const hasShownWelcome = useRef(false);

  const checkExistingAuthEarly = () => {
    try {
      const existingToken = storage.getItem('token');
      const userDataString = storage.getItem('profile');

      if (existingToken && userDataString) {
        const existingUser = JSON.parse(userDataString);
        if (existingUser && existingUser.email && existingUser.uid) {
          axios.defaults.headers.common['Authorization'] =
            `Bearer ${existingToken}`;
          axios.defaults.headers.common['dash-client-type'] = 'web';
          navigate(APP_CONFIG.homeRoute, { replace: true });
          return true;
        }
      }
    } catch (error) {
      console.warn('Failed to parse early auth check:', error);
    }
    return false;
  };

  const earlyRedirectRef = useRef(false);
  if (!earlyRedirectRef.current) {
    if (process.env.NODE_ENV === 'development') {
      debugAuth();
    }
    earlyRedirectRef.current = checkExistingAuthEarly();
  }

  useEffect(() => {
    const checkExistingAuth = () => {
      try {
        const existingToken = storage.getItem('token');
        const userDataString = storage.getItem('profile');

        if (existingToken && userDataString) {
          const existingUser = JSON.parse(userDataString);
          if (existingUser && existingUser.email && existingUser.uid) {
            axios.defaults.headers.common['Authorization'] =
              `Bearer ${existingToken}`;
            axios.defaults.headers.common['dash-client-type'] = 'web';
            navigate(APP_CONFIG.homeRoute, { replace: true });
          }
        }
      } catch (error) {
        console.warn('Failed to parse user data from storage:', error);
      }
    };

    if (!earlyRedirectRef.current && !user && !token) {
      checkExistingAuth();
    }
  }, [navigate, user, token]);

  useEffect(() => {
    if (user && token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.defaults.headers.common['dash-client-type'] = 'web';

      if (!hasShownWelcome.current) {
        toast.success('Berhasil masuk!', {
          description: `Selamat datang di ${APP_CONFIG.name}`,
        });
        hasShownWelcome.current = true;
      }

      navigate(APP_CONFIG.homeRoute, { replace: true });
    }
  }, [user, token, navigate]);

  const handleGoogleSignIn = async (): Promise<void> => {
    try {
      await signInWithGoogle();
    } catch (err) {}
  };

  return (
    <div className="min-h-screen bg-bg-white-0 flex flex-col">
      <header className="p-6">
        <div className="flex items-center justify-between">
          {/* eslint-disable-next-line react/style-prop-object -- DashLogo `style` is a color-variant prop, not the DOM style attribute */}
          <DashLogo variant="wordmark" style="black" size="md" title="Dash" />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <Card padding="none" className="w-[520px] p-8">
          <div className="flex justify-center mb-4">
            <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-(--theme-accent-alpha-10)">
              <ShieldCheck
                size={44}
                strokeWidth={1.5}
                className="text-accent"
                aria-hidden="true"
              />
            </div>
          </div>

          <div className="text-center mb-4">
            <p className="label-eyebrow mb-2">
              <span className="text-accent font-bold mr-3">·</span>
              Authentication
            </p>
            <h1 className="text-[26px] font-bold tracking-tight text-text-strong-950 mb-2">
              {APP_CONFIG.loginTitle}
            </h1>
            <p className="text-[14px] text-text-sub-600 leading-relaxed">
              {APP_CONFIG.loginDescription}
            </p>
          </div>

          <div className="text-center mb-5 border-t border-stroke-soft-200 pt-4">
            <p className="text-[12px] text-text-sub-600 font-mono tracking-tight">
              {getAllowedDomainLabel()}
            </p>
          </div>

          <Button
            tone="neutral"
            style="stroke"
            size="lg"
            onClick={handleGoogleSignIn}
            className="w-full"
          >
            <img
              src="/images/google-icon.png"
              alt="Google"
              className="w-5 h-5 mr-3"
            />
            Login with Google
          </Button>
        </Card>
      </div>

      <footer className="p-6 border-t border-stroke-soft-200">
        <div className="flex items-center justify-between text-xs text-text-sub-600 tabular">
          <span>{APP_CONFIG.copyright}</span>
          <span className="label-eyebrow">v1.0 · Internal</span>
        </div>
      </footer>
    </div>
  );
};

export default Index;
